import { useEffect, useRef } from "react";
import { useClerk } from "@clerk/clerk-react";

const JUST_SIGNED_OUT_KEY = "nombase_just_signed_out";
const RETURN_TO_KEY = "nombase_return_to";

/**
 * Sign the user out and land them on /sign-in.
 *
 * Defends against two failure modes observed in production:
 *
 *  (1) `clerk.signOut()` resolves before Clerk's React hooks across the
 *      tree have re-rendered with `isSignedIn: false`. The fresh page
 *      load that follows the navigation reads Clerk's cookies and may
 *      momentarily report the user as still authenticated. AuthPage's
 *      `useRedirectByRole` then auto-bounces the user back to the
 *      downstream app they just tried to log out of.
 *
 *  (2) The user originally arrived at the Gateway via
 *        `nombaseconnect.com/sign-in?return_to=https://mrp.nombaseconnect.com/`
 *      AuthPage stashes that return_to in sessionStorage. After signout,
 *      if the stash isn't cleared, `useRedirectByRole` will read it on
 *      the next /sign-in mount and rebound the user back to MRP.
 *
 * Mitigations:
 *
 *   a. Drop a `nombase_just_signed_out` flag in sessionStorage BEFORE
 *      calling signOut. `useRedirectByRole` checks this flag at the top
 *      of its effect; if present, it clears the flag and returns early,
 *      letting AuthPage render the sign-in form regardless of what
 *      Clerk's hooks momentarily report.
 *
 *   b. Clear any cached `nombase_return_to` so a stale redirect doesn't
 *      survive the signout.
 *
 *   c. 6s watchdog: if signOut() hangs (offline, network), navigate
 *      to /sign-in anyway. The marker still does its job on the next
 *      load — user sees a real form, not an auto-redirect.
 */
export default function LogoutPage() {
  const clerk = useClerk();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    // Order matters: set the flag BEFORE calling signOut so even if the
    // navigation fires on the synchronous catch path, the marker is in
    // place for AuthPage.
    try {
      // Timestamped marker. `useRedirectByRole` only honours markers
      // newer than SIGNOUT_MARKER_TTL_MS so that a stale marker (e.g.
      // user signs out, closes tab, returns hours later) doesn't
      // prevent a legitimate redirect on their next sign-in.
      window.sessionStorage.setItem(
        JUST_SIGNED_OUT_KEY,
        String(Date.now())
      );
      window.sessionStorage.removeItem(RETURN_TO_KEY);
    } catch {
      // sessionStorage disabled (private mode) — proceed; we'll still
      // navigate, but the auto-redirect-on-/sign-in defence is lost
      // for this user. Best-effort.
    }

    const target = `${window.location.origin}/sign-in`;

    const watchdog = window.setTimeout(() => {
      window.location.assign(target);
    }, 6000);

    Promise.resolve(clerk.signOut())
      .catch(() => {
        // signOut failed (network, offline, etc). Marker is still set,
        // so AuthPage will not auto-redirect even if Clerk's session
        // cookie survives the failure.
      })
      .finally(() => {
        window.clearTimeout(watchdog);
        window.location.assign(target);
      });
  }, [clerk]);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center bg-[#F7F8FA]"
      data-testid="logout-page"
    >
      <p className="text-sm text-[#64748B]" data-testid="logout-message">
        Signing you out…
      </p>
    </div>
  );
}
